import "core-js/modules/web.dom-collections.iterator.js";
import React, { useEffect, useState } from 'react';
import { StandardButton } from '@eika/button';
import { Modal, ModalTypes } from '@eika/modal';
import BoardActorsSection from '../containers/board-actors';
import CheckboxSourceOfIncomeSection from '../containers/checkbox-source-of-income';
import CorporateBeneficialOwnerActors from '../containers/corporate-beneficial-owner-actors';
import CorporateInformation from '../containers/corporate-information';
import CorporateTaxRelationSection from '../containers/corporate-tax-relations';
import CountryRelations from '../containers/countries';
import DynamicSection from '../containers/dynamics';
import FinancialInstitution from '../containers/financial-institution';
import PepSection from '../containers/pepRca';
import ReadonlyActors from '../containers/readonly-actors';
import SourceOfIncomeSection from '../containers/source-of-income';
import TaxRelationSection from '../containers/tax-relations';
import { SectionType } from '../generated-types';
import { getCurrentIndex, removeAllValuesContainingAhvIndexTextAndNotOtherValues, removeCurrentIndex, setCurrentIndex, setToken } from '../util/sessionStorage';
import CreateCaseFailed from './create-case-failed';
import CreateCaseRetry from './create-case-retry';
import ErrorMessage from './error-message';
import Finish from './finish';
import InfoSection from './info-section';
import NotFound from './not-found';
import OverlayLoader from './overlay-loader';
import TaxSigning from './signing/tax-signing';
import CaseSummary from './summary/case-summary';
import ProcessSummary from './summary/process-summary';
import WelcomePage from './welcome-page';
import '../styles.scss';
require('../styles.scss');
var SectionTypeEnum = /*#__PURE__*/function (SectionTypeEnum) {
  SectionTypeEnum["SECTIONS"] = "sections";
  SectionTypeEnum["DONE"] = "done";
  SectionTypeEnum["FAILED_CREATING_CASE"] = "failedCreatingCase";
  SectionTypeEnum["FAILED_CREATING_CASE_RETRY"] = "failedCreatingCaseRetry";
  return SectionTypeEnum;
}(SectionTypeEnum || {});
const TAB_INDEX_ZERO = 0;
/**
 * just enough characters to write: lønn (wage/salary)
 */
const SOURCE_OF_INCOME_COMMENT_LENGTH = 4;
const KYCForm = _ref => {
  let {
    token,
    getForm,
    getCountries,
    keepAliveAction,
    keepAlive,
    completeDeclaration,
    form,
    countries,
    submitForm,
    statusCallback,
    finishedText,
    onFinishRedirectURL,
    onCaseCompletionFailedURL,
    hidePrintInformationInFinalPage,
    showStepIndicator,
    goBackText,
    goBackCallback,
    hideOnFetchFail,
    context,
    minCommentLength = 10,
    resetForm
  } = _ref;
  const [currentSectionIndex, setCurrentSectionIndex] = useState(getCurrentIndex(token) >= 0 ? getCurrentIndex(token) : 0);
  const [currentPageType, setCurrentPageType] = useState(SectionTypeEnum.SECTIONS);
  const [loading, setLoading] = useState(true);
  const [notified, setNotified] = useState(false);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  useEffect(() => {
    resetForm();
    removeAllValuesContainingAhvIndexTextAndNotOtherValues(token);
    setToken(token);
  }, [token, context, submitForm.kycStatus, resetForm]);
  useEffect(() => {
    if (token) getForm(context);
  }, [getForm, token, context]);
  useEffect(() => {
    getCountries();
  }, [getCountries]);
  useEffect(() => {
    const keepAliveAsync = async intervalId => {
      await keepAliveAction(intervalId);
    };
    const interval = 60000;
    const maxDuration = 1000 * 60 * 60 * 4;
    const start = Date.now();
    const createdIntervalId = setInterval(() => {
      const now = Date.now();
      if (now - start > maxDuration) {
        clearInterval(createdIntervalId);
      }
      return keepAliveAsync(createdIntervalId);
    }, interval);
    return () => {
      clearInterval(createdIntervalId);
    };
  }, [form.loaded, keepAliveAction]);
  useEffect(() => {
    if (submitForm.completed && submitForm.kycStatus) {
      setCurrentPageType(SectionTypeEnum.DONE);
      removeCurrentIndex(token);
      if (statusCallback && !notified) {
        statusCallback(submitForm.kycStatus);
        setNotified(true);
      }
    }
  }, [submitForm, statusCallback, setCurrentPageType, token, notified]);
  useEffect(() => {
    if (submitForm.formCompletionInProgress) {
      setLoading(true);
    }
  }, [submitForm]);
  useEffect(() => {
    if (form.loaded && countries.countries.length > 0 && !submitForm.formCompletionInProgress && !form.error) {
      setLoading(false);
      window.scrollTo(0, 0);
    }
  }, [form, countries, submitForm]);
  useEffect(() => {
    if (submitForm.failedDuringCompletion) {
      if (onCaseCompletionFailedURL) {
        setCurrentPageType(SectionTypeEnum.FAILED_CREATING_CASE);
      } else {
        setCurrentPageType(SectionTypeEnum.FAILED_CREATING_CASE_RETRY);
      }
    }
  }, [onCaseCompletionFailedURL, submitForm.failedDuringCompletion]);
  useEffect(() => {
    var _keepAlive$keepAliveE;
    if (form.error && hideOnFetchFail) {
      hideOnFetchFail();
    }
    if ((keepAlive === null || keepAlive === void 0 || (_keepAlive$keepAliveE = keepAlive.keepAliveError) === null || _keepAlive$keepAliveE === void 0 ? void 0 : _keepAlive$keepAliveE.status) === 401) {
      setShowSessionExpiredModal(true);
    }
  }, [form.error, hideOnFetchFail, keepAlive]);
  useEffect(() => {
    if (!loading) {
      const formStepNumberHeader = document.querySelectorAll('h3, .form-step-number');
      if (formStepNumberHeader) {
        const firstHeader = formStepNumberHeader[0];
        if (firstHeader) {
          firstHeader.scrollIntoView();
        } else {
          window.scrollTo(0, 0);
        }
      } else {
        window.scrollTo(0, 0);
      }
    }
  }, [loading, currentSectionIndex]);
  const setCurrentIndexes = newSectionIndex => {
    setCurrentSectionIndex(newSectionIndex);
    setCurrentIndex(newSectionIndex, token);
  };
  const hideShowSessionExpiredModal = () => {
    setShowSessionExpiredModal(false);
  };
  const numberOfSections = form.loaded && form.sections.length;
  const currentSectionNumber = currentSectionIndex + 1;
  const currentStepString = "".concat(currentSectionNumber, "/").concat(numberOfSections);
  const goToNextPage = () => {
    const nextIndex = currentSectionIndex + 1;
    if (currentPageType === SectionTypeEnum.SECTIONS) {
      if (form.loaded && currentSectionIndex === form.sections.length - 1) {
        if (!submitForm.completed && !submitForm.formCompletionInProgress) {
          completeDeclaration();
        }
      } else {
        setCurrentIndexes(nextIndex);
      }
    }
  };
  const submitButtonText = currentPageType === SectionTypeEnum.DONE ? 'Ferdig' : 'Neste';
  const renderSection = (section, index) => {
    if (!section) {
      setCurrentSectionIndex(0);
      removeCurrentIndex(token);
      return /*#__PURE__*/React.createElement(React.Fragment, null);
    }
    switch (section.typeInfo) {
      case SectionType.corporateInformationSection:
        return /*#__PURE__*/React.createElement(CorporateInformation, {
          section: section,
          goToNext: goToNextPage
        });
      case SectionType.welcome:
        return /*#__PURE__*/React.createElement(WelcomePage, {
          onSubmit: goToNextPage,
          submitButtonText: submitButtonText,
          goBackText: goBackText,
          goBackCallback: goBackCallback
        });
      case SectionType.welcomeWithProducts:
        return /*#__PURE__*/React.createElement(WelcomePage, {
          onSubmit: goToNextPage,
          submitButtonText: submitButtonText,
          goBackText: goBackText,
          goBackCallback: goBackCallback,
          products: section.products
        });
      case SectionType.corporateFinancialInstitution:
        return /*#__PURE__*/React.createElement(FinancialInstitution, {
          goToNext: goToNextPage,
          section: section
        });
      case SectionType.informationSection:
        return /*#__PURE__*/React.createElement(InfoSection, {
          section: section,
          onButtonClick: goToNextPage,
          statusCallback: statusCallback,
          onFinishRedirectURL: onFinishRedirectURL
        });
      case SectionType.country:
        return /*#__PURE__*/React.createElement(CountryRelations, {
          countrySection: section,
          onSubmit: goToNextPage
        });
      case SectionType.dynamic:
        return /*#__PURE__*/React.createElement(DynamicSection, {
          dynamicSection: section,
          onSubmit: goToNextPage,
          submitButtonText: submitButtonText,
          sectionIndex: index
        });
      case SectionType.pep:
        return /*#__PURE__*/React.createElement(PepSection, {
          onSubmit: goToNextPage,
          input: section,
          minCommentLength: minCommentLength
        });
      case SectionType.tax:
        return /*#__PURE__*/React.createElement(TaxRelationSection, {
          taxRelationsSection: section,
          onSubmit: goToNextPage,
          submitButtonText: submitButtonText
        });
      case SectionType.corporateTax:
        return /*#__PURE__*/React.createElement(CorporateTaxRelationSection, {
          corporateTaxRelationsSection: section,
          onSubmit: goToNextPage,
          submitButtonText: submitButtonText
        });
      case SectionType.sourceOfIncome:
        return /*#__PURE__*/React.createElement(SourceOfIncomeSection, {
          onSubmit: goToNextPage,
          submitButtonText: submitButtonText,
          sourceOfIncomeQuestionSection: section,
          minCommentLength: SOURCE_OF_INCOME_COMMENT_LENGTH
        });
      case SectionType.checkboxSourceOfIncome:
        return /*#__PURE__*/React.createElement(CheckboxSourceOfIncomeSection, {
          onSubmit: goToNextPage,
          submitButtonText: submitButtonText,
          checkboxSourceOfIncomeQuestionSection: section
        });
      case SectionType.corporateBoardActorsV3:
        return /*#__PURE__*/React.createElement(BoardActorsSection, {
          onSubmit: goToNextPage,
          corporateBoardActorsSection: section
        });
      case SectionType.corporateBeneficialOwnerActorsV3:
        return /*#__PURE__*/React.createElement(CorporateBeneficialOwnerActors, {
          onSubmit: goToNextPage,
          submitButtonText: submitButtonText,
          corporateBeneficialOwnerActorsSection: section
        });
      case SectionType.readonlyActorsSection:
        return /*#__PURE__*/React.createElement(ReadonlyActors, {
          section: section,
          onButtonClick: goToNextPage
        });
      case SectionType.processSummary:
        return /*#__PURE__*/React.createElement(ProcessSummary, {
          summarySection: section,
          buttonAction: goToNextPage,
          submitButtonText: submitButtonText
        });
      case SectionType.caseSummary:
        return /*#__PURE__*/React.createElement(CaseSummary, {
          summarySection: section,
          buttonAction: goToNextPage,
          submitButtonText: submitButtonText
        });
      case SectionType.taxSigning:
        return /*#__PURE__*/React.createElement(TaxSigning, {
          afterSigning: goToNextPage
        });
      default:
        return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h3", null, "Not implemented section"), /*#__PURE__*/React.createElement("div", null, "Section type: ".concat(section.typeInfo)), /*#__PURE__*/React.createElement(StandardButton, {
          className: "progress-next-button",
          onClick: goToNextPage
        }, "Neste"));
    }
  };
  const getFormSectionForCurrentIndex = () => {
    return form.loaded && form.sections && form.sections.length > 0 ? renderSection(form.sections[currentSectionIndex], currentSectionIndex) : undefined;
  };
  const getSection = () => {
    switch (currentPageType) {
      case SectionTypeEnum.SECTIONS:
        return /*#__PURE__*/React.createElement(React.Fragment, null, showStepIndicator && /*#__PURE__*/React.createElement("h3", {
          className: "form-step-number",
          tabIndex: TAB_INDEX_ZERO
        }, form.loaded && currentStepString), getFormSectionForCurrentIndex());
      case SectionTypeEnum.DONE:
        return /*#__PURE__*/React.createElement(Finish, {
          finishedText: finishedText,
          onFinishRedirectURL: onFinishRedirectURL,
          hidePrintInformationInFinalPage: hidePrintInformationInFinalPage
        });
      case SectionTypeEnum.FAILED_CREATING_CASE:
        return /*#__PURE__*/React.createElement(CreateCaseFailed, {
          onCaseCompletionFailedURL: onCaseCompletionFailedURL
        });
      case SectionTypeEnum.FAILED_CREATING_CASE_RETRY:
        return /*#__PURE__*/React.createElement(CreateCaseRetry, {
          completeDeclaration: completeDeclaration
        });
      default:
        return /*#__PURE__*/React.createElement(NotFound, null);
    }
  };
  const fetchForm = () => {
    setLoading(true);
    getForm(context);
  };
  return /*#__PURE__*/React.createElement("div", null, loading && !form.error && !countries.failedOnFetch && /*#__PURE__*/React.createElement(OverlayLoader, null), submitForm.failedDuringCompletion && currentPageType !== SectionTypeEnum.FAILED_CREATING_CASE && /*#__PURE__*/React.createElement(ErrorMessage, {
    text: "Klarte ikke \xE5 registrere svarene. Vennligst pr\xF8v igjen. Kontakt banken dersom problemet vedvarer"
  }), form.error && /*#__PURE__*/React.createElement("div", {
    className: "extra-margin-top center-text"
  }, /*#__PURE__*/React.createElement(ErrorMessage, {
    text: "Klarte ikke \xE5 hente skjemaet. Vennligst pr\xF8v \xE5 hente skjemaet p\xE5 nytt. Dersom problemet vedvarer m\xE5 du kontakte banken"
  }), /*#__PURE__*/React.createElement("div", {
    className: "extra-margin-top"
  }, /*#__PURE__*/React.createElement(StandardButton, {
    onClick: fetchForm
  }, "Hent skjema p\xE5 nytt"))), showSessionExpiredModal ? /*#__PURE__*/React.createElement(Modal, {
    id: "session-expired-modal",
    onClose: hideShowSessionExpiredModal,
    show: showSessionExpiredModal,
    type: ModalTypes.STANDARD,
    appNamespace: "kyc-declaration.app"
  }, /*#__PURE__*/React.createElement("div", {
    className: "card"
  }, /*#__PURE__*/React.createElement("div", {
    className: "sub-card-content"
  }, /*#__PURE__*/React.createElement("div", {
    className: "card-header"
  }, /*#__PURE__*/React.createElement("h2", {
    className: "small-h2"
  }, "\xD8kten utl\xF8pt")), /*#__PURE__*/React.createElement("div", {
    className: "card-body"
  }, /*#__PURE__*/React.createElement("p", null, "Vennligst logg p\xE5 igjen"), /*#__PURE__*/React.createElement("div", {
    className: "session-expired-done-wrapper"
  }, /*#__PURE__*/React.createElement(StandardButton, {
    type: "button",
    className: "mll btn btn-primary",
    onClick: hideShowSessionExpiredModal,
    id: "session-expired-done-button"
  }, "Lukk")))))) : /*#__PURE__*/React.createElement("div", null, getSection()));
};
export default KYCForm;